import { useCallback, useEffect, useState } from 'react';

import { logEvents as logEventsApi } from '@/helpers/ApiHelper';
import type { ListLogEventsParams } from '@/helpers/ApiHelper';
import type { PaginatedLogEvents } from '@/types/log_event';

export type { ListLogEventsParams };

export interface UseLogEventsResult {
  data: PaginatedLogEvents | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useLogEvents(params: ListLogEventsParams = {}): UseLogEventsResult {
  const [data, setData] = useState<PaginatedLogEvents | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Stable serialized key so the effect reruns only when params values change.
  const paramsKey = JSON.stringify(params);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await logEventsApi.list(JSON.parse(paramsKey) as ListLogEventsParams);
      setData(result);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setIsLoading(false);
    }
  }, [paramsKey]);

  useEffect(() => {
    let cancelled = false;

    setIsLoading(true);
    logEventsApi
      .list(JSON.parse(paramsKey) as ListLogEventsParams)
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setError(null);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e : new Error(String(e)));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [paramsKey]);

  return { data, isLoading, error, refetch: fetchData };
}
